import { createSale } from '@/api/counter.api';
import { isApiError } from '@/lib/errors';
import { isOnline, reportUnreachable } from './connectivity';
import { log } from './log';
import { notify } from './notify';
import { outbox, type OutboxEntry } from './outbox';

/**
 * Drains the outbox, oldest first, one at a time.
 *
 * **Serial, not parallel.** Bills must reach the server in the order the
 * counter took them, or the server's own series runs out of step with the
 * numbers already printed and handed over.
 *
 * **Stops at the first retryable failure.** If the network drops halfway
 * through, pushing on would burn attempts on entries that were always going to
 * fail, and could let a later bill overtake an earlier one.
 */

export type SyncResult = { sent: number; failed: number; remaining: number };

let running = false;
const listeners = new Set<() => void>();

export function onSyncChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const announce = () => {
  for (const listener of listeners) listener();
};

/**
 * Whether retrying could ever help.
 *
 * A 4xx is the server's settled opinion — a duplicate bill number, a
 * prescription-only medicine with no prescriber, stock that is genuinely gone.
 * Retrying it forever would hide it. It is marked failed and surfaced instead.
 * A 5xx or a dead socket is the line, not the request.
 */
function isPermanent(error: unknown): boolean {
  return isApiError(error) && error.status >= 400 && error.status < 500;
}

export async function drainOutbox(): Promise<SyncResult> {
  if (running || !isOnline()) {
    return { sent: 0, failed: 0, remaining: await outbox.count() };
  }

  running = true;
  announce();
  log.sync('drain started');

  let sent = 0;
  let failed = 0;

  try {
    const pending = await outbox.pending();

    for (const entry of pending) {
      if (entry.failed) {
        failed += 1;
        continue;
      }
      if (!isOnline()) break;

      try {
        log.sync(`sending #${entry.seq} ${entry.billNumber} (attempt ${entry.attempts + 1})`);
        await send(entry);
        await outbox.remove(entry.seq!);
        sent += 1;
      } catch (error) {
        const permanent = isPermanent(error);
        log.warn(
          'sync',
          `#${entry.seq} ${permanent ? 'REFUSED, will not retry' : 'failed, will retry'}`,
          error instanceof Error ? error.message : error,
        );
        await outbox.update({
          ...entry,
          attempts: entry.attempts + 1,
          lastError: error instanceof Error ? error.message : String(error),
          failed: permanent,
        });
        if (permanent) {
          failed += 1;
          /*
           * The one thing worth interrupting someone for. This bill is in a
           * customer's hands and now exists nowhere but this device, and the
           * sync that refused it ran in the background — very likely while this
           * tab was behind something else. A toast would go unseen.
           */
          notify(
            'A bill was refused',
            `${entry.billNumber} could not be sent. Open Settings › Unsent bills.`,
          );
          // A settled refusal does not block the ones behind it.
          continue;
        }
        // The line went again. Say so at once rather than letting the pill
        // claim a connection until the next probe, and leave the rest queued.
        reportUnreachable();
        break;
      } finally {
        announce();
      }
    }
  } finally {
    running = false;
    announce();
  }

  const remaining = await outbox.count();
  log.sync(`drain finished: ${sent} sent, ${failed} refused, ${remaining} still queued`);
  return { sent, failed, remaining };
}

function send(entry: OutboxEntry): Promise<unknown> {
  switch (entry.kind) {
    case 'sale':
      /*
       * The idempotency key was generated when the bill was taken and never
       * changes. A reply that was sent but never arrived leaves the sale
       * recorded on the server and the entry still queued here; replaying with
       * the same key returns that original sale rather than billing the
       * customer a second time.
       */
      return createSale(entry.pharmacyId, entry.payload, entry.idempotencyKey);
    default:
      throw new Error(`Nothing knows how to send a ${entry.kind}`);
  }
}

export function isSyncing(): boolean {
  return running;
}
